import React, { useMemo, useState } from 'react';
import { CaretUpDown, Check, MagnifyingGlass } from '@phosphor-icons/react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '../ui/command';
import { cn } from '../../lib/utils';

// Full IANA time-zone list from the browser runtime (no dependency).
// Falls back to a small list on very old engines that lack supportedValuesOf.
function getZones() {
  try {
    const z = Intl.supportedValuesOf('timeZone');
    if (Array.isArray(z) && z.length) return z;
  } catch { /* older engine */ }
  return ['UTC', 'Europe/Riga', 'Europe/London', 'America/New_York', 'Asia/Tokyo'];
}

// Current UTC offset label for a zone, e.g. "UTC+02:00".
function offsetLabel(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(new Date());
    const name = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    return name.replace(/^GMT/, 'UTC');
  } catch {
    return '';
  }
}

export function TimezoneCombobox({ value, onChange, testid = 'admin-org-timezone' }) {
  const [open, setOpen] = useState(false);
  const zones = useMemo(getZones, []);
  const offset = value ? offsetLabel(value) : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="w-full flex items-center justify-between gap-2 rounded-lg border border-stone_line bg-white px-3 py-2 text-sm text-ink hover:border-sage focus:outline-none focus:ring-2 focus:ring-sage/40 transition-colors"
        >
          <span className={cn('font-mono truncate', !value && 'text-ink-muted font-sans')}>
            {value || 'Select a time zone…'}
          </span>
          <span className="flex items-center gap-2 shrink-0">
            {offset && <span className="text-[11px] text-ink-muted font-mono">{offset}</span>}
            <CaretUpDown size={16} className="text-ink-muted" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[280px]" align="start">
        <Command
          filter={(val, search) => (val.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0)}
        >
          <div className="flex items-center border-b border-stone_line px-3">
            <MagnifyingGlass size={15} className="text-ink-muted shrink-0" />
            <CommandInput
              placeholder="Search time zone…"
              data-testid={`${testid}-search`}
              className="border-0 focus:ring-0"
            />
          </div>
          <CommandList className="max-h-64">
            <CommandEmpty>No time zone found.</CommandEmpty>
            <CommandGroup>
              {zones.map((tz) => (
                <CommandItem
                  key={tz}
                  value={tz}
                  data-testid={`${testid}-option-${tz}`}
                  onSelect={() => { onChange(tz); setOpen(false); }}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Check size={15} className={cn('shrink-0 text-sage', value === tz ? 'opacity-100' : 'opacity-0')} />
                    <span className="font-mono text-xs truncate">{tz.replace(/_/g, ' ')}</span>
                  </span>
                  <span className="text-[11px] text-ink-muted font-mono shrink-0">{offsetLabel(tz)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
